/**
 * 选词预览（下一步"录音听写"的输入）—— **本轮只读预览，不录音、不做听写流程**。
 *
 * ## 为什么要先给家长看一遍
 *
 * "今天听写三年级上册 · 随机 20 个词"这种话，家长只有在**看见具体是哪 20 个词**时
 * 才能确认取对了。后端把"补全默认值之后实际用的条件"回显在 `applied` 里
 * （例如只传 `{}` 时 `applied.gradeCodes = ['primary_2']`），
 * 所以这里必须把 `applied` 显示出来 —— 否则家长不知道这次听写覆盖了什么。
 *
 * ## 「年级」选择器为什么在这里，而不在别处（2026-10）
 *
 * "按谁的年级取词"**只在这件事上才有意义**，所以选择器就放在这里，而且让家长
 * **直接选年级**（一年级…高中三年级，可多选）—— 不让他"选孩子再让系统推年级"，
 * 那对家长是绕弯。界面上的默认值来自内部选定的孩子（`defaultGradeCode`），
 * 但**界面上只出现年级**，不出现孩子，也不出现"锚点"这种内部概念。
 * 语义（三条都写在界面文案里）：
 *   · 没动过选择器 → 用默认年级（`defaultGradeCode`）；
 *   · 全不选 → **不限年级**（显式传全部年级，而不是"偷偷用默认年级"）；
 *   · 选中哪几个 → 就取哪几个年级的词。
 *
 * ## 取词来源：第四步接上了「错词」（原来是 501）
 *
 * 第三步时 `source: 'wrong'` **刻意**返回 501 + `DICTATION_MISTAKES_NOT_READY`
 * （而不是假装成功返回空列表）。第四步把错词本建好之后，这里有了三个来源：
 *
 * | 来源 | 取什么 |
 * | --- | --- |
 * | `all` | 全部词（默认） |
 * | `wrong_first` | **错词优先**：错词按"下次该复习的时间"排前面，剩余名额补新词 |
 * | `wrong` | **只听写错词** |
 *
 * ⚠️ 排序规则必须在界面上说清楚（"错词优先"到底怎么排）—— 那句话优先用
 *   后端返回的 `note`（它才知道这次实际取了什么）。"系统为什么这么排"
 *   是家长第一个会问的问题，含糊过去他就会怀疑系统在乱排。
 *
 * 每个词上还会标出"在不在错词本里、错了几次"（`word.mistake`），
 * 三种取值（对象 / `null` 确实不在 / `undefined` 没拿到）在界面上**分开渲染**。
 */
import { useState } from 'react';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { IconAlertTriangle } from '../ui/Icons';
import { GRADE_CODES, GRADES, describeTextbookLabel, gradeLabel } from '@fht/shared';
import { pickGradeCodesForRequest, selectedPickGrades } from '../../lib/dictationAnchor';
import {
  countLabel,
  describeListState,
  semesterLabel,
  sourceLabel,
  wordCategoryLabel,
} from '../../lib/api/dictation';
import type { PickAppliedView, PickedWordView, TextbookView } from '../../lib/api/dictation';

/** 一条错词信息 → 一行小字（"写错 2 次 · 今天该复习"）。 */
function describeWordMistake(mistake: {
  wrongCount: number;
  missingCount: number;
  due: boolean | null;
}): string {
  const parts: string[] = [];
  if (mistake.wrongCount > 0) parts.push(`写错 ${mistake.wrongCount} 次`);
  if (mistake.missingCount > 0) parts.push(`没写出来 ${mistake.missingCount} 次`);
  /** `due === null` = 没拿到"到没到期"，那就不说 —— 而不是说"没到期"。 */
  if (mistake.due === true) parts.push('今天该复习');
  return parts.length === 0 ? '在错词本里' : parts.join(' · ');
}

export interface PickPanelProps {
  textbooks: TextbookView[] | null;
  /**
   * 选词默认选中的年级（内部选定的孩子身上的年级，见 `lib/dictationAnchor.ts`）。
   * 空串 = 还没拿到 → 选择器不预选，界面提示"不选 = 不限年级"。
   */
  defaultGradeCode: string;
  applied: PickAppliedView | null;
  words: PickedWordView[] | null;
  total: number | null;
  /**
   * 这次用到的错词总数（`null` = 没拿到 / 这个来源没有这个概念）。
   * `source='all'` 时后端不给它，所以界面上**不显示**这个徽章 —— 而不是显示 0。
   */
  wrongTotal: number | null;
  /** 后端给的那句排序说明（`source` 涉及错词时才有）。 */
  note: string | null;
  /** 这个孩子错词本里**还没掌握**的词数（`null` = 没拿到）。 */
  openMistakeCount: number | null;
  /** 这个孩子**今天该复习**的错词数（`null` = 没拿到）。 */
  dueMistakeCount: number | null;
  missing: boolean;
  loading: boolean;
  error: string | null;
  /** 后端说"这个功能还没做"（501）时的人话。 */
  notReadyMessage: string | null;
  /**
   * 「这个字有哪几个音」的候选（第五步：只听某个读音的词）。
   * 拿不到时传 `null` —— 那一块**不显示**，而不是显示一个空下拉。
   */
  readingOptions?: { char: string; readings: string[] }[] | null;
  onPick: (payload: {
    textbookId?: string;
    gradeCodes?: string[];
    semesters?: string[];
    categories?: string[];
    fromTextbookOnly?: boolean;
    /** **按表筛**（识字表 / 写字表）。 */
    listTypes?: string[];
    /** **只听某个读音的词**。 */
    readings?: { char?: string; reading: string }[];
    /** **只看多音字相关的词**。 */
    isPolyphonic?: boolean;
    randomCount?: number;
    source?: 'all' | 'wrong_first' | 'wrong';
  }) => void;
}

const SEMESTER_OPTIONS = ['上', '下', '不分'];
const CATEGORY_OPTIONS = [
  { value: 'common', label: '常用词' },
  { value: 'phrase', label: '词语' },
  { value: 'idiom', label: '成语' },
];
/**
 * 字表两张（用户原话："可以选择《识字表》来听写，也可以按照《写字表》来听写"）。
 *
 * ⚠️ 词语表**不在这里**：它靠「只看课本词语表」那个开关（`fromTextbookOnly`），
 *    因为一个词"是不是来自词语表"是词自己的属性，不是字的收录表。
 */
const LIST_TYPE_OPTIONS = [
  { value: 'shizi', label: '识字表' },
  { value: 'xiezi', label: '写字表' },
];

/** 把 `applied` 说成一句人话（家长一眼能看懂这次取了什么）。 */
export function describeApplied(applied: PickAppliedView, total: number | null): string {
  const parts: string[] = [];
  /**
   * 年级：全选（12 个年级都在）就是"不限年级"，说成一句话比列 12 个年级名清楚；
   * 后端也可能回一个空数组（它自己没补默认值）—— 那同样是不限。
   */
  const allGrades = GRADE_CODES.every((code) => applied.gradeCodes.includes(code));
  parts.push(
    applied.gradeCodes.length === 0 || allGrades
      ? '全部年级'
      : applied.gradeCodes.map((code) => gradeLabel(code)).join('、'),
  );
  parts.push(
    applied.semesters.length > 0
      ? applied.semesters.map((semester) => semesterLabel(semester)).join('、')
      : '全部学期',
  );
  parts.push(
    applied.categories.length > 0
      ? applied.categories.map((category) => wordCategoryLabel(category)).join('、')
      : '全部类别',
  );
  if (applied.fromTextbookOnly === true) parts.push('只看课本词语表');
  /** 表维度（第五步）：选了才说，没选就不说（说了等于没说）。 */
  if (applied.listTypes.length > 0) {
    parts.push(
      `只在 ${applied.listTypes
        .map((type) => LIST_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type)
        .join('、')} 收录的字里找`,
    );
  }
  /** 读音维度（第五步）。 */
  if (applied.readings.length > 0) {
    parts.push(
      `只看读音：${applied.readings
        .map((entry) => `${entry.char ?? '任意字'} 读 ${entry.reading}`)
        .join('、')}`,
    );
  }
  if (applied.polyphonicOnly) parts.push('只看多音字相关的词');
  if (applied.lessonIds.length > 0) parts.push(`指定了 ${applied.lessonIds.length} 节课`);
  if (applied.randomCount !== null) parts.push(`随机 ${applied.randomCount} 个`);
  else parts.push('按文字顺序');
  if (applied.seed !== null) parts.push(`seed=${applied.seed}（同一份可复现）`);
  const count = total === null ? '—' : String(total);
  return `${parts.join(' · ')}（符合条件的词共 ${count} 个）`;
}

export function DictationPickPanel({
  textbooks,
  defaultGradeCode,
  applied,
  words,
  total,
  wrongTotal,
  note,
  openMistakeCount,
  dueMistakeCount,
  missing,
  loading,
  error,
  notReadyMessage,
  readingOptions,
  onPick,
}: PickPanelProps) {
  const [textbookId, setTextbookId] = useState('');
  /**
   * 年级选择：`null` = 家长**还没动过**（用默认年级）。
   * 一旦点过就变成具体数组，**空数组 = 明确表示"不限年级"**。
   * 这两条语义（界面高亮什么、真正发什么）都由 `lib/dictationAnchor.ts` 的纯函数决定
   * （有单测），这里只负责渲染 —— 免得"看到的"和"发出去的"不一致。
   */
  const [gradeChoice, setGradeChoice] = useState<string[] | null>(null);
  const gradeCodes = selectedPickGrades(gradeChoice, defaultGradeCode);
  const [semesters, setSemesters] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [fromTextbookOnly, setFromTextbookOnly] = useState(false);
  /** **按表听写**（第五步）：空数组 = 不限。 */
  const [listTypes, setListTypes] = useState<string[]>([]);
  /** **只听某个读音的词**（第五步）：`{char, reading}`。 */
  const [readingChoice, setReadingChoice] = useState<{ char: string; reading: string } | null>(null);
  /** **专门听写多音字**（第五步）。 */
  const [polyphonicOnly, setPolyphonicOnly] = useState(false);
  const [randomCount, setRandomCount] = useState('20');
  /** 取词来源（第四步接上了错词）。 */
  const [source, setSource] = useState<'all' | 'wrong_first' | 'wrong'>('all');

  const toggle = (list: string[], value: string, setter: (next: string[]) => void) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  };

  const state = describeListState(words, total);

  const pick = () =>
    onPick({
      ...(textbookId === '' ? {} : { textbookId }),
      /**
       * ⚠️ 年级**永远显式传**（全不选 → 全部年级 = 不限年级）：见
       * `pickGradeCodesForRequest` 的注释 —— 空数组会被后端当成"没传"而回落到
       * 默认年级，那正是"看到的和发出去的不一致"。
       */
      gradeCodes: pickGradeCodesForRequest(gradeChoice, defaultGradeCode),
      ...(semesters.length === 0 ? {} : { semesters }),
      ...(categories.length === 0 ? {} : { categories }),
      ...(fromTextbookOnly ? { fromTextbookOnly: true } : {}),
      /** **按表听写**（识字表 / 写字表）—— 第五步新增。 */
      ...(listTypes.length === 0 ? {} : { listTypes }),
      /** **只听某个读音的词**。`char` 也带上：只认"这个字"读这个音。 */
      ...(readingChoice === null
        ? {}
        : { readings: [{ char: readingChoice.char, reading: readingChoice.reading }] }),
      ...(polyphonicOnly ? { isPolyphonic: true } : {}),
      ...(randomCount.trim() === '' ? {} : { randomCount: Number(randomCount) }),
      source,
    });

  /** 错词本里到底有没有东西 —— 决定"按错词"那两个选项要不要提示"还是空的"。 */
  const mistakeBookEmpty = openMistakeCount === 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="选词预览"
          description="这一步只看会取到哪些词。真正的录音与孩子听写是下一步的事，本轮不做。"
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select label="课本版本" value={textbookId} onChange={(event) => setTextbookId(event.target.value)}>
            <option value="">全部版本</option>
            {/*
              ⚠️ 带版次（家长端也能看到）：他要知道"这套字表是照哪本书的哪一版录的"。
                 同一个课本名的两个版次是两套字表，界面必须分得开。
            */}
            {(textbooks ?? []).map((textbook) => (
              <option key={textbook.id} value={textbook.id}>
                {describeTextbookLabel(textbook)}
              </option>
            ))}
          </Select>
          <Input
            label="随机取几个（留空 = 按顺序取）"
            value={randomCount}
            inputMode="numeric"
            onChange={(event) => setRandomCount(event.target.value)}
            hint="最多 200"
          />
          <Select
            label="取词来源"
            value={source}
            onChange={(event) =>
              setSource(event.target.value === 'wrong_first' ? 'wrong_first' : event.target.value === 'wrong' ? 'wrong' : 'all')
            }
            hint={
              openMistakeCount === null
                ? '错词本的数量没拿到（不影响选词）'
                : mistakeBookEmpty
                  ? '错词本现在是空的 —— 先批改一次听写'
                  : `错词本里还有 ${openMistakeCount} 个没掌握${
                      dueMistakeCount === null ? '' : `，今天该复习 ${dueMistakeCount} 个`
                    }`
            }
          >
            <option value="all">全部词语</option>
            <option value="wrong_first">错词优先（错词排前面，再补新词）</option>
            <option value="wrong">只听写错词</option>
          </Select>
          <label className="flex items-end gap-2 pb-2 text-sm text-slate-600">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300"
              checked={fromTextbookOnly}
              onChange={(event) => setFromTextbookOnly(event.target.checked)}
            />
            只看课本词语表
          </label>
        </div>

        <div className="mt-3 space-y-2">
          <div>
            {/*
              年级：**家长直接选年级**（不选孩子）。措辞只说两件事：
              ① 可以多选 ② 全不选 = 不限年级。默认值下面单独一句话交代来源。
            */}
            <p className="text-xs font-medium text-slate-500">年级（可多选；全不选 = 不限年级）</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {GRADES.map((grade) => (
                <button
                  key={grade.code}
                  type="button"
                  aria-pressed={gradeCodes.includes(grade.code)}
                  onClick={() =>
                    setGradeChoice(
                      gradeCodes.includes(grade.code)
                        ? gradeCodes.filter((code) => code !== grade.code)
                        : [...gradeCodes, grade.code],
                    )
                  }
                  className={
                    gradeCodes.includes(grade.code)
                      ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                      : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                  }
                >
                  {gradeLabel(grade.code)}
                </button>
              ))}
            </div>
            {/*
              默认值是怎么来的（一句话）：它来自内部选定的那个孩子，但这里**只报年级**。
              ⚠️ 不要说"锚点"、也不要说"某个孩子当锚点"——家长要的是年级，不是机制。
            */}
            {defaultGradeCode !== '' && (
              <p className="mt-1 text-[11px] text-slate-400">
                默认选中{gradeLabel(defaultGradeCode)} —— 按家里孩子的年级自动带上，可以随时改。
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-xs font-medium text-slate-500">学期</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {SEMESTER_OPTIONS.map((semester) => (
                  <button
                    key={semester}
                    type="button"
                    onClick={() => toggle(semesters, semester, setSemesters)}
                    className={
                      semesters.includes(semester)
                        ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                        : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                    }
                  >
                    {semesterLabel(semester)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">类别</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {CATEGORY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggle(categories, option.value, setCategories)}
                    className={
                      categories.includes(option.value)
                        ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                        : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/*
            ---------- 第五步新增的两块：**按表听写** 与 **按读音听写** ----------
            用户原话："可以按《识字表》来听写，也可以按照《写字表》来听写"、
            "如果一个字是多音字……这样也可以专门听写多音字"。
          */}
          <div className="mt-3 flex flex-wrap gap-6">
            <div>
              <p className="text-xs font-medium text-slate-500">按哪张表听写（识字表 / 写字表）</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {LIST_TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={listTypes.includes(option.value)}
                    onClick={() => toggle(listTypes, option.value, setListTypes)}
                    className={
                      listTypes.includes(option.value)
                        ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                        : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                词里有任何一个字出现在选中的表上就算（两张都不选 = 不限）。
              </p>
            </div>

            <div>
              <p className="text-xs font-medium text-slate-500">只听某个读音</p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {readingOptions === null || readingOptions === undefined ? (
                  <span className="text-[11px] text-slate-400">
                    读音选项没拿到（不影响其它筛选）
                  </span>
                ) : readingOptions.length === 0 ? (
                  <span className="text-[11px] text-slate-400">
                    还没有多音字可选 —— 先录一份字表
                  </span>
                ) : (
                  readingOptions.map((option) =>
                    option.readings.map((reading) => {
                      const active =
                        readingChoice?.char === option.char && readingChoice.reading === reading;
                      return (
                        <button
                          key={`${option.char}-${reading}`}
                          type="button"
                          aria-pressed={active}
                          onClick={() =>
                            setReadingChoice(active ? null : { char: option.char, reading })
                          }
                          className={
                            active
                              ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                              : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                          }
                        >
                          {option.char} · <span className="font-mono">{reading}</span>
                        </button>
                      );
                    }),
                  )
                )}
              </div>
              <label className="mt-1 flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={polyphonicOnly}
                  onChange={(event) => setPolyphonicOnly(event.target.checked)}
                />
                专门听写多音字（只看有字读了非默认读音的词）
              </label>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button loading={loading} onClick={pick}>
            看会取到哪些词
          </Button>
          <span className="min-w-0 flex-1 text-xs text-slate-500">
            {/*
              ⚠️ 排序规则必须在界面上说清楚（"错词优先"到底怎么排）。
              这句话优先用**后端给的 `note`**（它才知道这次实际取了什么），
              没拿到时用下面这句固定的说法 —— 但两者说的是同一条规则。
            */}
            {note ??
              (source === 'wrong_first'
                ? '错词优先：先把错词本里还没掌握的词按「下次该复习的时间」从早到晚排前面，剩下的名额补这个范围内的新词（按文字顺序）。'
                : source === 'wrong'
                  ? '只听写错词：词全部来自错词本，按「下次该复习的时间」从早到晚排。'
                  : '全部词语：按文字顺序（填了「随机取几个」则随机）。')}
          </span>
        </div>

        {notReadyMessage !== null && (
          <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <span className="mt-0.5 shrink-0">
              <IconAlertTriangle size={16} />
            </span>
            <span>{notReadyMessage}</span>
          </p>
        )}
        {error !== null && (
          <p role="alert" className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}
      </Card>

      {(applied !== null || loading || words !== null) && (
        <Card>
          <CardHeader
            title="这次实际取到的词"
            description={applied !== null ? describeApplied(applied, total) : '正在取词…'}
            actionAlign="start"
            action={
              <div className="flex basis-full flex-wrap items-center gap-2">
                <Badge tone={total === null ? 'warn' : 'neutral'}>
                  取到 {countLabel(words === null ? null : words.length)} 个
                  {total !== null ? ` / 共 ${total} 个` : ''}
                </Badge>
                {/*
                  错词数：**只有后端给了才显示**（`source='all'` 时它没有这个概念，
                  显示 0 会把"没有概念"冒充成"一个错词都没有"）。
                */}
                {wrongTotal !== null && (
                  <Badge tone={wrongTotal === 0 ? 'neutral' : 'warn'}>
                    错词本里符合条件的 {wrongTotal} 个
                  </Badge>
                )}
                <Badge tone="neutral">{sourceLabel(applied?.source ?? source)}</Badge>
              </div>
            }
          />

          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : missing ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              没拿到取词结果（响应里没有 `words` 数组）—— 这不是"一个词都没取到"。
            </p>
          ) : state.state === 'mismatch' ? (
            <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {state.message}
            </p>
          ) : words !== null && words.length === 0 ? (
            <EmptyState
              icon="🔍"
              title="这个条件下没有取到词"
              description="可能是这个年级学期还没有录字词，或者筛得太窄了。先去「批量导入」录一份字表。"
            />
          ) : (
            <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {(words ?? []).map((word) => (
                <li key={word.id} className="min-w-0 rounded-xl border border-slate-200 px-3 py-2">
                  {/*
                    ⚠️ 2026-10-17：**拼音在上、词在下**（与「听写字表」的字卡同一个排法）。
                       拼音行固定一行高（`h-4 leading-4` + `truncate`）：空读音时写的仍是
                       「（没有读音）」，所以卡片高度不会被"有没有拼音"拉开。
                  */}
                  <p className="h-4 truncate font-mono text-xs leading-4 text-slate-500">
                    {/* 空串读音要说出来（`includePinyin: false` 时后端就是给空串） */}
                    {word.pinyin === '' ? '（没有读音）' : word.pinyin}
                  </p>
                  <p className="truncate text-base font-semibold text-slate-900">{word.wordText}</p>
                  <p className="mt-0.5 truncate text-[11px] text-slate-400">
                    {wordCategoryLabel(word.category)}
                    {word.fromTextbook ? ' · 词语表' : ''}
                    {word.lessons.length > 0 ? ` · ${word.lessons[0]!.title}` : ''}
                  </p>
                  {/*
                    逐位读音（第五步）：**"专门听写多音字"这件事在界面上的证据** ——
                    家长要能看出这个词为什么被取到（是哪个字读了非默认音）。
                    ⚠️ `charReadings === null` = **没拿到**（说一声），不是"没有读音"。
                  */}
                  {word.charReadings === null ? (
                    <p className="mt-0.5 text-[11px] text-amber-600">逐字读音没拿到</p>
                  ) : word.charReadings.some((entry) => entry.nonDefault) ? (
                    <p className="mt-0.5 truncate text-[11px] font-medium text-brand-700">
                      读音：
                      {word.charReadings
                        .map(
                          (entry) =>
                            `${entry.charText}${entry.reading === '' ? '?' : entry.reading}${
                              entry.nonDefault ? '（多音）' : ''
                            }`,
                        )
                        .join(' ')}
                    </p>
                  ) : null}
                  {/*
                    错词标记：**这是"错词优先"这件事在界面上的证据** ——
                    家长要能看出哪些是错词、它错了几次、该不该复习。
                    ⚠️ 三种取值分开渲染：`undefined`（没拿到）说"历史没拿到"，
                    `null`（确实不是错词）**什么都不显示**。
                  */}
                  {word.mistake === undefined ? (
                    <p className="mt-0.5 text-[11px] text-amber-600">错词记录没拿到</p>
                  ) : word.mistake !== null ? (
                    <p className="mt-0.5 text-[11px] font-medium text-amber-700">
                      错词：{describeWordMistake(word.mistake)}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
